// Schritt 3 des Übergabe-Assistenten — Zähler-Kontrolle: Tabelle aller Zähler der
// Einheit. Bereits im Rundgang erfasste Zähler (Schritt 2) erscheinen mit Haken und
// Stand, alle übrigen mit einem Eingabefeld direkt in der Zeile ("nachtragbar"). Ein
// nicht erfasster Zähler ist kein Fehler, soll aber nicht untergehen — daher der
// Hinweis, aber kein Zwang.
//
// Fix-Runde 1: Der Stand wird jetzt bei jedem Tastendruck committet (vorher erst bei
// onBlur — ging beim App-Wechsel während des Tippens verloren). Damit das nicht
// wieder in das ursprüngliche Problem läuft (offeneZaehler() zählt einen Zähler schon
// als "erfasst", sobald irgendein Eintrag existiert, unabhängig vom Stand — ein Zähler
// würde also nach dem ersten Tastendruck die Zeile wechseln und den Fokus verlieren),
// gibt es jetzt nur noch EINE Zeilen-Liste in stabiler Reihenfolge (`stammdaten`);
// "erfasst" ist rein optisch (Haken + grüne Tönung), keine eigene Sektion mehr.
//
// Optik: Frosted Glass — jede Zeile ist eine eigene kleine Glaskarte, erfasste Zeilen
// mit grünem Tönungs-Akzent.
//
// Task 2 (Etappe 1.5): Die Zuordnung zur Einheit läuft über passtZurEinheit()
// (tolerant gegen Schreibweise/Leerzeichen) statt exaktem Gleichheitsvergleich —
// dafür werden alle aktiven Zähler des Objekts geladen und clientseitig
// aufgeteilt. Zähler, deren Bezeichnung trotzdem nicht passt, verschwinden nicht
// spurlos, sondern landen im aufklappbaren Bereich „Weitere Zähler dieses
// Objekts" und bleiben dort erfassbar.
import React, { useCallback, useEffect, useState } from 'react';
import { Camera, CaretDown, Check, CheckCircle, MapPin, WarningCircle } from '@phosphor-icons/react';
import { skaliereBild } from '../../../lib/uebergabe/bild';
import FotoVorschau from './FotoVorschau';
import { zaehlerStil } from '../../../lib/uebergabeAnsicht/darstellung';
import { ZAEHLER_ICON, istTouch, schriftAuf } from './assistent/Rundgangteile';
import { supabase } from '../../../lib/supabase/client';
import type { LokalerEntwurf } from '../../../lib/uebergabe/lokal';
import { offeneZaehler, zuZaehlerEintrag, type ZaehlerStammdaten } from '../../../lib/uebergabe/zaehlerZuordnung';
import { passtZurEinheit } from '../../../lib/uebergabe/einheitVergleich';
import LadeHinweis from './LadeHinweis';

interface SchrittZaehlerKontrolleProps {
  entwurf: LokalerEntwurf;
  setEntwurf: (updater: LokalerEntwurf | ((vorher: LokalerEntwurf) => LokalerEntwurf)) => void;
}

const LEER: React.CSSProperties = { borderRadius: 26, padding: '36px 20px', textAlign: 'center', fontSize: 14, fontWeight: 600, color: 'var(--ink3)' };

const SchrittZaehlerKontrolle: React.FC<SchrittZaehlerKontrolleProps> = ({ entwurf, setEntwurf }) => {
  const { protokoll } = entwurf;
  const [stammdaten, setStammdaten] = useState<ZaehlerStammdaten[]>([]);
  const [weitereZaehler, setWeitereZaehler] = useState<ZaehlerStammdaten[]>([]);
  const [zeigeWeitere, setZeigeWeitere] = useState(false);
  const [laedt, setLaedt] = useState(true);
  const [fehler, setFehler] = useState(false);

  const ladeStammdaten = useCallback(async () => {
    setLaedt(true);
    setFehler(false);
    if (!protokoll.objekt_id || !protokoll.einheit_id) { setStammdaten([]); setWeitereZaehler([]); setLaedt(false); return; }
    const { data: einheit, error: einheitFehler } = await supabase
      .from('objekt_einheiten').select('bezeichnung').eq('id', protokoll.einheit_id).maybeSingle();
    if (einheitFehler) { setLaedt(false); setFehler(true); return; }
    if (!einheit?.bezeichnung) { setStammdaten([]); setWeitereZaehler([]); setLaedt(false); return; }
    // Alle aktiven Zähler des Objekts laden und clientseitig über passtZurEinheit
    // in "gehört zu dieser Einheit" und "übrige Zähler des Objekts" aufteilen —
    // statt serverseitig auf Gleichheit zu vergleichen (Freitext, uneinheitlich
    // geschrieben). Die übrigen bleiben im aufklappbaren Bereich erfassbar, damit
    // ein Zähler mit falsch zugeordneter Bezeichnung nicht komplett untergeht.
    const { data, error } = await supabase
      .from('zaehler')
      .select('id, zaehler_nummer, typ, standort, einheit_bezeichnung')
      .eq('objekt_id', protokoll.objekt_id).eq('aktiv', true);
    setLaedt(false);
    if (error) { setFehler(true); return; }
    const alle = (data ?? []) as ZaehlerStammdaten[];
    setStammdaten(alle.filter(z => passtZurEinheit(z.einheit_bezeichnung ?? null, einheit.bezeichnung)));
    setWeitereZaehler(alle.filter(z => !passtZurEinheit(z.einheit_bezeichnung ?? null, einheit.bezeichnung)));
  }, [protokoll.objekt_id, protokoll.einheit_id]);
  useEffect(() => { void ladeStammdaten(); }, [ladeStammdaten]);

  // Committet bei jeder Eingabe (kein onBlur) — legt bei Bedarf einen Eintrag ohne
  // Raumbezug an. Leere Eingabe ohne bestehenden Eintrag erzeugt keine leere Zeile.
  const standAendern = (z: ZaehlerStammdaten, wert: string) => {
    setEntwurf(e => {
      const bestehend = e.protokoll.zaehler.find(x => x.zaehler_id === z.id);
      if (!wert.trim() && !bestehend) return e;
      const zaehler = bestehend
        ? e.protokoll.zaehler.map(x => (x.zaehler_id === z.id ? { ...x, stand: wert } : x))
        : [...e.protokoll.zaehler, { ...zuZaehlerEintrag(z, null), stand: wert }];
      return { ...e, protokoll: { ...e.protokoll, zaehler } };
    });
  };

  // Foto vom Zählerstand (Freigabe 17.10.2026, wie im Rundgang): Bild verkleinern, im lokalen Entwurf
  // ablegen und am Zählereintrag vermerken — fehlt der Eintrag noch, wird er wie beim Stand angelegt.
  const fotoHinzufuegen = async (z: ZaehlerStammdaten, datei: File) => {
    const skaliert = await skaliereBild(datei);
    const pfad = `${entwurf.id}/fotos/${crypto.randomUUID()}.jpg`;
    setEntwurf(e => {
      const bestehend = e.protokoll.zaehler.find(x => x.zaehler_id === z.id);
      const zaehler = bestehend
        ? e.protokoll.zaehler.map(x => (x.zaehler_id === z.id ? { ...x, foto: pfad } : x))
        : [...e.protokoll.zaehler, { ...zuZaehlerEintrag(z, null), foto: pfad }];
      return { ...e, fotos: { ...e.fotos, [pfad]: skaliert }, protokoll: { ...e.protokoll, zaehler } };
    });
  };

  if (laedt) return <div className="immo-glas" style={LEER}>Zähler werden geladen…</div>;
  if (fehler) return <LadeHinweis text="Zähler der Einheit konnten nicht geladen werden — Verbindung prüfen." aufErneutVersuchen={ladeStammdaten} />;
  if (!protokoll.einheit_id) return <div className="immo-glas" style={LEER}>Erst im Schritt „Rahmen" eine Einheit wählen.</div>;
  if (stammdaten.length === 0 && weitereZaehler.length === 0) {
    return <div className="immo-glas" style={LEER}>Für dieses Objekt sind keine Zähler hinterlegt.</div>;
  }

  const offene = offeneZaehler(stammdaten, protokoll.zaehler);

  const touch = istTouch();
  const zeile = (z: ZaehlerStammdaten) => {
    const eintrag = protokoll.zaehler.find(x => x.zaehler_id === z.id);
    const stand = eintrag?.stand ?? '';
    const hatStand = stand.trim() !== '';
    const hatFoto = !!(eintrag?.foto && entwurf.fotos[eintrag.foto]);
    const stil = zaehlerStil(z.typ);
    const I = ZAEHLER_ICON[stil.symbol];
    return (
      <div key={z.id} className="flex min-w-0 flex-col" style={{ gap: 10, padding: 12, borderRadius: 20, background: 'var(--fill)',
        boxShadow: hatStand ? 'inset 0 0 0 2px var(--ok)' : 'inset 0 0 0 1px var(--line)' }}>
        <div className="flex min-w-0 items-start" style={{ gap: 10 }}>
          <span aria-hidden className="inline-flex shrink-0 items-center justify-center" style={{ width: 38, height: 38, borderRadius: 11, background: stil.farbe, color: schriftAuf(stil.farbe) }}>
            <I size={19} weight="fill" />
          </span>
          <span className="flex min-w-0 flex-1 flex-col" style={{ gap: 1 }}>
            <span className="flex items-center truncate" style={{ gap: 6, fontSize: 15, fontWeight: 800 }}>
              {z.typ}{hatStand && <CheckCircle size={15} weight="fill" color="var(--ok)" className="shrink-0" />}
            </span>
            <span className="truncate" style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink3)', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}>{z.zaehler_nummer}</span>
          </span>
          <span className="inline-flex shrink-0 items-center" style={{ height: 26, padding: '0 9px', gap: 4, borderRadius: 13, fontSize: 11.5, fontWeight: 700, background: 'var(--fill2)', color: 'var(--ink2)', maxWidth: '45%' }}>
            <MapPin size={12} weight="fill" className="shrink-0" /><span className="truncate">{eintrag?.raum || z.standort || 'Standort unbekannt'}</span>
          </span>
        </div>
        <div className="flex items-center" style={{ gap: 8 }}>
          <input inputMode="numeric" placeholder="Stand" value={stand} onChange={e => standAendern(z, e.target.value)} aria-label={`Stand ${z.typ} ${z.zaehler_nummer}`}
            className="min-w-0 flex-1"
            style={{ height: touch ? 56 : 52, padding: '0 14px', borderRadius: 14, border: 'none', outline: 'none', background: 'var(--bg)',
              boxShadow: 'inset 0 0 0 1px var(--line)', fontFamily: 'inherit', fontSize: 22, fontWeight: 800, color: 'var(--ink)' }} />
          {hatFoto && <FotoVorschau blob={entwurf.fotos[eintrag!.foto!]} groesse={touch ? 56 : 52} />}
          <label data-tip="Foto vom Zählerstand" className="inline-flex shrink-0 cursor-pointer items-center justify-center"
            style={{ width: touch ? 56 : 52, height: touch ? 56 : 52, borderRadius: 14, background: hatFoto ? 'var(--ok)' : 'var(--fill2)', color: hatFoto ? '#fff' : 'var(--ink2)' }}>
            {hatFoto ? <Check size={18} weight="bold" /> : <Camera size={20} weight="fill" />}
            <input type="file" accept="image/*" capture="environment" className="hidden" aria-label={`Foto vom Zählerstand ${z.typ} ${z.zaehler_nummer}`}
              onChange={e => { const datei = e.target.files?.[0]; if (datei) void fotoHinzufuegen(z, datei); e.target.value = ''; }} />
          </label>
        </div>
      </div>
    );
  };
  const raster = { display: 'grid', gap: 10, gridTemplateColumns: 'repeat(auto-fill, minmax(min(280px, 100%), 1fr))' } as const;

  return (
    <>
      <section aria-label="Zähler der Einheit" className="immo-glas flex flex-col" style={{ borderRadius: 26, padding: 18, gap: 12 }}>
        {stammdaten.length > 0 ? (
          <>
            <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink3)' }}>Alle Zähler der Einheit</span>
            <div style={raster}>{stammdaten.map(zeile)}</div>
          </>
        ) : (
          <p className="m-0 text-center" style={{ padding: '18px 0', fontSize: 14, fontWeight: 600, color: 'var(--ink3)' }}>
            Für diese Einheit sind keine passenden Zähler hinterlegt — ggf. unten bei „Weitere Zähler dieses Objekts" nachsehen.
          </p>
        )}
        {offene.length > 0 && (
          <p className="m-0 flex items-start" style={{ gap: 8, padding: '10px 14px', borderRadius: 14, background: 'var(--warn-soft)', fontSize: 13, fontWeight: 700, color: 'var(--warn)' }}>
            <WarningCircle size={16} weight="fill" className="mt-[1px] shrink-0" />{offene.length} Zähler ohne Stand — beim Abschluss fehlen sie im Protokoll.
          </p>
        )}
      </section>
      {weitereZaehler.length > 0 && (
        <section aria-label="Weitere Zähler dieses Objekts" className="immo-glas flex flex-col" style={{ borderRadius: 26, padding: '8px 18px', gap: 10 }}>
          <button type="button" onClick={() => setZeigeWeitere(v => !v)} aria-expanded={zeigeWeitere} className="flex w-full items-center justify-between"
            style={{ minHeight: 48, gap: 8, border: 'none', background: 'transparent', cursor: 'pointer', fontFamily: 'inherit', fontSize: 14, fontWeight: 800, color: 'var(--ink)' }}>
            <span>Weitere Zähler dieses Objekts ({weitereZaehler.length})</span>
            <CaretDown size={16} weight="bold" color="var(--ink3)" style={{ transform: zeigeWeitere ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }} />
          </button>
          {zeigeWeitere && (
            <div className="flex flex-col" style={{ gap: 10, paddingBottom: 10 }}>
              <p className="m-0" style={{ fontSize: 13, lineHeight: 1.45, color: 'var(--ink2)' }}>
                Diese Zähler sind im Objekt hinterlegt, ihre Einheiten-Bezeichnung passt aber nicht zur gewählten Einheit —
                z.B. wegen eines Tippfehlers in den Stammdaten. Bei Bedarf hier trotzdem erfassen.
              </p>
              <div style={raster}>{weitereZaehler.map(zeile)}</div>
            </div>
          )}
        </section>
      )}
    </>
  );
};

export default SchrittZaehlerKontrolle;
